import React from "react";
import { LOGO_URL, HERO_BG_URL } from "../lib/constants";
import { Button } from "../components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Music4, Video, Sparkles, Megaphone, Shield } from "lucide-react";
import Header from "../components/Header";
import PartnerCarousel from "../components/PartnerCarousel";
import { getUser } from "../lib/api";

export default function Landing() {
  const navigate = useNavigate();
  const user = getUser();

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden relative">
      {/* Background image with overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_BG_URL})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        {/* grain */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
          }}
        />
      </div>

      <Header />

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-6 md:pt-12 pb-6">
        <div className="grid lg:grid-cols-12 gap-6 items-center">
          {/* Left: hero text */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Sparkles className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/70">
                Danza Sportiva
              </span>
            </div>

            <h1 className="font-black tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl xl:text-[4rem]">
              <span className="block bg-gradient-to-r from-[#FF5722] via-[#FF9800] to-[#00E5FF] bg-clip-text text-transparent">
                Music & Video
              </span>
              <span className="block bg-gradient-to-r from-[#00E5FF] via-[#7C4DFF] to-[#FF4081] bg-clip-text text-transparent">
                Danza Sportiva
              </span>
            </h1>

            <p
              className="mt-8 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed"
              data-testid="landing-subtitle"
            >
              Piattaforma per l'invio delle Musiche e dei Video dedicati alla Danza Sportiva
              dove sia possibile competere con Musica Propria. Per i Video dove sia richiesto
              per la competizione un Video in sostituzione della Scenografia.
            </p>

            <div className="mt-10 flex flex-col items-start gap-3">
              <Button
                onClick={() => navigate("/competizioni")}
                data-testid="landing-entra-btn"
                className="group relative h-14 px-10 rounded-full text-base font-bold bg-gradient-to-r from-[#FF5722] to-[#00E5FF] hover:from-[#00E5FF] hover:to-[#FF5722] text-white shadow-[0_0_30px_rgba(255,87,34,0.45)] hover:shadow-[0_0_45px_rgba(0,229,255,0.65)] hover:-translate-y-0.5 transition-all duration-300 border-0"
              >
                <span className="tracking-[0.3em]">ENTRA ED INVIA</span>
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              <Link to="/assistenza" data-testid="landing-assistance-link" className="text-white/60 hover:text-white text-sm transition-colors underline-offset-4 hover:underline">
                Hai bisogno di aiuto? Apri un ticket
              </Link>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-5">
                <Music4 className="w-6 h-6 text-orange-400 mb-2" />
                <div className="text-sm font-semibold">Invio Musica</div>
                <div className="text-xs text-white/50 mt-1">MP3 — gestione organizzata</div>
              </div>
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-5">
                <Video className="w-6 h-6 text-cyan-400 mb-2" />
                <div className="text-sm font-semibold">Invio Video</div>
                <div className="text-xs text-white/50 mt-1">MP4 — fino a 100MB</div>
              </div>
              <Link
                to="/assistenza"
                data-testid="landing-request-competition-card"
                className="group backdrop-blur-xl bg-white/5 border border-violet-400/30 rounded-2xl p-5 transition-all hover:border-violet-400/60 hover:bg-violet-500/10 hover:-translate-y-0.5 relative overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full bg-violet-500/20 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />
                <Megaphone className="w-6 h-6 text-violet-300 mb-2 relative" />
                <div className="text-sm font-semibold relative">
                  INSERISCI LA TUA COMPETIZIONE
                </div>
                <div className="text-xs text-white/50 mt-1 relative leading-relaxed">
                  Per richiedere l'inserimento della competizione apri un ticket
                </div>
                <div className="text-xs text-violet-300 mt-2 inline-flex items-center gap-1 relative">
                  Apri ticket{" "}
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            </div>
          </div>

          {/* Right: logo display */}
          <div className="lg:col-span-5 flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-10 bg-gradient-to-tr from-orange-500/30 via-cyan-500/20 to-blue-700/30 blur-3xl rounded-full" />
              <img
                src={LOGO_URL}
                alt="LMKT"
                className="relative w-56 h-56 md:w-72 md:h-72 object-contain drop-shadow-[0_0_60px_rgba(255,87,34,0.4)] animate-[float_6s_ease-in-out_infinite]"
              />
            </div>
          </div>
        </div>
      </main>

      <PartnerCarousel />

      {/* Footer with Area Riservata login link */}
      {!user && (
        <footer className="relative z-10 border-t border-white/10 bg-black/50 backdrop-blur-sm mt-6">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-white/40 text-center sm:text-left">
              © {new Date().getFullYear()} LMKT — Music & Video SEND
            </div>
            <Link
              to="/admin/login"
              data-testid="landing-footer-admin-link"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 hover:border-cyan-400/50 transition-all"
            >
              <Shield className="w-4 h-4 text-cyan-300 group-hover:text-cyan-200" />
              <span className="text-sm font-semibold tracking-wider text-white/80 group-hover:text-white">
                AREA RISERVATA
              </span>
              <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </footer>
      )}

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(2deg); }
        }
      `}</style>
    </div>
  );
}
